import { getRandomQuote } from '@focuslee/shared';
import { Box, Collapse, IconButton, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import { FC, ReactNode, useMemo, useState } from 'react';
import type { ListCategory, ListGoal } from '../../client/types';
import { getColor } from '../../helpers/color';
import { mapTargetPlanToNumber, markNames, marks } from '../../helpers/goals';
import { GoalsListViewSkeleton } from '../goals/skeleton/GoalsListViewSkeleton';
import { Page } from '../Page';
import { CategoryIcon } from './CategoryIcon';
import { CommonIcon } from './CommonIcon';
import { LevelProgress } from './LevelProgress';
import { WeekProgress } from './WeekProgress';

type GoalsListViewProps = {
  activeGoals: ListGoal[];
  goalCompletion: string;
  categoryMap: Record<string, ListCategory>;
  loading?: boolean;
  footer?: ReactNode;
};

export const GoalsListView: FC<GoalsListViewProps> = ({ activeGoals, goalCompletion, categoryMap, loading, footer }) => {
    const quote = useMemo(() => getRandomQuote(), []);
    const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
    const toggleExpand = (id: string) => setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));

    if (loading) return <GoalsListViewSkeleton footer={footer} />;
    return (
        <Page back insetEnabled={false} footer={footer}>
            <Stack alignItems="center" justifyContent="center">
            <Text style={{ textAlign: 'center', fontSize: '12px', lineHeight: '18px', maxWidth: "50%", color: 'var(--tgui--hint_color)' }}>
                {quote}
            </Text>
            </Stack>
            <Stack justifyContent="center" flex={1} direction="column" alignItems="center" px={2}>
            <Stack spacing={1} sx={{ width: '100%', mt: 2 }}>
                {activeGoals.map(g => {
                const cat = categoryMap[g.categoryId];
                const target = g.l3Target;
                const completed = g.completedXP;
                const isExpanded = expandedMap[g.id] || false;
                return (
                    <Stack key={g.id} direction="column" spacing={1} sx={{ width: '100%', mt: 2 }}>
                        <Box display="flex" alignItems="start">
                            <Stack flex={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                                    <Stack direction="row" spacing={1}>
                                        <Box sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: 1,
                                            background: getColor(cat?.iconColor ?? ''),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CategoryIcon
                                                icon={cat?.icon || 'emoji_events'}
                                                iconColor={cat?.iconColor || ''}
                                                customIcon={cat?.customIcon}
                                                type="filled"
                                                size={14}
                                                color="var(--tg-theme-text-color)"
                                            />
                                        </Box>
                                        <Text>{g.name}</Text>
                                    </Stack>
                                    <IconButton size="small" onClick={() => toggleExpand(g.id)}>
                                        <CommonIcon icon={isExpanded ? 'expand_less' : 'expand_more'} color="var(--tgui--hint_color)" type="filled" size={20} />
                                    </IconButton>
                                </Stack>
                                <Stack spacing={0.5} sx={{ mt: 1 }}>
                                    <Stack direction="row" justifyContent="space-between" width="100%">
                                        <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '12px', lineHeight: '12px' }}>
                                            {completed}/{target} XP
                                        </Text>
                                        <Stack direction="row" justifyContent="flex-end" spacing={0.2}>
                                            <CommonIcon icon={marks[mapTargetPlanToNumber(g.targetPlan ?? null) - 1]} color="var(--tgui--hint_color)" type="filled" size={12} />
                                            <Text style={{ color: 'var(--tgui--hint_color)', fontSize: '12px', lineHeight: '12px' }}>
                                                {markNames[mapTargetPlanToNumber(g.targetPlan ?? null) - 1]}
                                            </Text>
                                        </Stack>
                                    </Stack>
                                    <LevelProgress goal={g} />
                                </Stack>
                            </Stack>
                        </Box>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <WeekProgress goal={g} goalCompletion={goalCompletion} />
                        </Collapse>
                    </Stack>
                );
                })}
            </Stack>
            </Stack>
        </Page>
    );
};
